/**
 * Date/time formatting for the whole SPA, pinned to Europe/Madrid.
 *
 * The timezone is written here and nowhere else: the Wetaca week closes on
 * Thursday 08:15 Madrid time, so a label rendered in the browser's own timezone
 * would name a different day for anyone outside Spain.
 */

const MADRID_TZ = 'Europe/Madrid'

/**
 * Formats a remaining milliseconds value into a human-readable countdown string.
 * Returns "Cerrado" when msRemaining <= 0.
 *
 * Examples:
 *   formatCountdown(0)           → "Cerrado"
 *   formatCountdown(-1)          → "Cerrado"
 *   formatCountdown(3661000)     → "1h 1m 1s"
 *   formatCountdown(90061000)    → "1d 1h 1m"  (days shown → seconds omitted)
 *   formatCountdown(3600000)     → "1h 0m 0s"
 */
export const formatCountdown = (msRemaining: number): string => {
  if (msRemaining <= 0) return 'Cerrado'

  const totalSeconds = Math.floor(msRemaining / 1000)
  const days = Math.floor(totalSeconds / 86400)
  const hours = Math.floor((totalSeconds % 86400) / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  if (days > 0) {
    // When days are present, show d/h/m (omit seconds for readability)
    return `${days}d ${hours}h ${minutes}m`
  }
  return `${hours}h ${minutes}m ${seconds}s`
}

/** "Semana 07 · 2026" — zero-padded so a list of weeks lines up and sorts. */
export const formatWeekLabel = (isoYear: number, isoWeek: number): string =>
  `Semana ${isoWeek.toString().padStart(2, '0')} · ${isoYear}`

/**
 * Formats a Date as "lunes, 29 jun" in Madrid TZ.
 */
export const formatMadridDate = (date: Date): string =>
  new Intl.DateTimeFormat('es-ES', {
    weekday: 'long',
    day: 'numeric',
    month: 'short',
    timeZone: MADRID_TZ,
  }).format(date)

/**
 * Formats a Date as "HH:MM" in Madrid TZ.
 */
export const formatMadridTime = (date: Date): string =>
  new Intl.DateTimeFormat('es-ES', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: MADRID_TZ,
  }).format(date)

/**
 * Formats a cutoffAt Date as "mié, 24 jun · 13:00" in Madrid TZ.
 */
export const formatCutoffLabel = (date: Date): string => {
  const dayPart = new Intl.DateTimeFormat('es-ES', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: MADRID_TZ,
  }).format(date)
  const timePart = formatMadridTime(date)
  return `${dayPart} · ${timePart}`
}
